import { Check } from "lucide-react";
import type { SetupStep } from "@/lib/setup/resume";
import { cn } from "@/lib/utils";

const PILLS = [
  { step: 1, title: "Question" },
  { step: 2, title: "Seeds & scope" },
  { step: 3, title: "Check the grading" },
  { step: 4, title: "Start" },
] as const;

/** `current` 4 = Start is running. A skipped ③ is hollow and can't be revisited
 *  (Start has already begun when it's skipped). */
export function SetupStepper({ current, skipped, onBack }: {
  current: 1 | 2 | 3 | 4;
  skipped: boolean;
  onBack: (step: SetupStep) => void;
}) {
  return (
    <nav aria-label="Setup steps">
      <ol className="flex flex-wrap items-center gap-2">
        {PILLS.map(({ step, title }) => {
          const isCurrent = step === current;
          const isSkipped = skipped && step === 3;
          const done = step < current && !isSkipped;
          const canGo = done && step <= 3 && current < 4;
          return (
            <li key={step} className="flex items-center gap-2">
              {step > 1 && <span aria-hidden className="h-px w-4 bg-border" />}
              <button
                type="button"
                disabled={!canGo}
                onClick={() => onBack(step as SetupStep)}
                aria-current={isCurrent ? "step" : undefined}
                className={cn(
                  "flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm disabled:cursor-default",
                  isCurrent && "border-primary bg-primary font-medium text-primary-foreground",
                  done && "border-primary/40 bg-primary/10",
                  canGo && "hover:bg-primary/20",
                  isSkipped && "border-dashed text-muted-foreground",
                  !isCurrent && !done && !isSkipped && "text-muted-foreground",
                )}
              >
                {done ? <Check aria-hidden className="size-3.5" /> : <span aria-hidden>{step}</span>}
                {title}
                {isSkipped && <span className="text-xs"> · skipped</span>}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
